import { CNS_API_BASE } from "@/config";
import {
  CNSBillingCheckoutConfirmation,
  CNSBillingCheckoutSession,
  CNSBillingPeriodCycle,
  CNSBillingPortalSession,
  CNSCreditPackSummary,
  CNSWorkspaceBillingSubscription,
} from "@/models/cns";
import { throwOnProblemResponse } from "@/types/problem-detail";

/**
 * Starts a Stripe Checkout session for a plan upgrade. The caller is expected to redirect the
 * browser to {@link CNSBillingCheckoutSession.sessionUrl}; Stripe lands the user back on
 * `/dashboard/billing/{success,cancel}` and the webhook is authoritative for state changes.
 *
 * @throws ApiErrorLike with `code='plan-not-purchasable'` when the plan template has no positive
 *         monthly/yearly price (free plans flow through {@code createWorkspaceBillingProfile}).
 * @throws ApiErrorLike with `code='checkout-session-creation-failed'` on Stripe-side errors.
 */
export async function createCheckoutSession(
  workspaceId: string,
  planTemplatePublicId: string,
  billingPeriodCycle: CNSBillingPeriodCycle
): Promise<CNSBillingCheckoutSession> {
  const res = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/billing/checkout-session`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ planTemplatePublicId, billingPeriodCycle }),
    }
  );
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to start checkout session");
  }
  return res.json();
}

/**
 * Starts a Stripe Checkout session for a credit-pack top-up purchase. Same redirect contract as
 * {@link createCheckoutSession}.
 */
export async function createTopupCheckoutSession(
  workspaceId: string,
  creditPackPublicId: string
): Promise<CNSBillingCheckoutSession> {
  const res = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/billing/topup-checkout-session`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ creditPackPublicId }),
    }
  );
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to start top-up checkout session");
  }
  return res.json();
}

/**
 * Mints a Stripe Customer Portal session so the workspace owner can manage their subscription
 * (cancel, switch tier, update payment method). Caller is expected to redirect to
 * {@link CNSBillingPortalSession.portalUrl}.
 *
 * @throws ApiErrorLike with `code='customer-portal-unavailable'` when Stripe is not reachable.
 * @throws ApiErrorLike with `code='subscription-not-found'` when the workspace has no active subscription.
 */
export async function createCustomerPortalSession(
  workspaceId: string
): Promise<CNSBillingPortalSession> {
  const res = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/billing/customer-portal-session`,
    {
      method: "POST",
      credentials: "include",
    }
  );
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to open customer portal");
  }
  return res.json();
}

/**
 * Confirms a checkout session after the Stripe success redirect. Reads authoritative session state from
 * Stripe server-side and, for completed subscription checkouts, idempotently fulfils the purchase so the
 * success page can resolve in a single call rather than polling. The webhook remains authoritative; this
 * call is safe to repeat.
 *
 * @throws ApiErrorLike with `code='resource-not-found'` when the session is unknown or does not belong
 *         to the workspace.
 * @throws ApiErrorLike with `code='payment-provider-unavailable'` when Stripe is not configured.
 */
export async function confirmCheckoutSession(
  workspaceId: string,
  checkoutSessionId: string
): Promise<CNSBillingCheckoutConfirmation> {
  const res = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/billing/checkout-session/${encodeURIComponent(checkoutSessionId)}/confirm`,
    {
      method: "POST",
      credentials: "include",
    }
  );
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to confirm checkout session");
  }
  return res.json();
}

/**
 * Reads the composite billing/subscription summary for a workspace. Returns `subscriptionStatus`
 * `NONE` for free Explorer workspaces with no payment-provider subscription on file.
 */
export async function fetchWorkspaceBillingSubscription(
  workspaceId: string
): Promise<CNSWorkspaceBillingSubscription> {
  const res = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/billing/subscription`,
    { credentials: "include" }
  );
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to load billing summary");
  }
  return res.json();
}

/**
 * Reads the active credit-pack SKU catalog. Authenticated users only; backend filters out archived
 * and inactive packs.
 */
export async function fetchCreditPacks(): Promise<CNSCreditPackSummary[]> {
  const res = await fetch(`${CNS_API_BASE}/credit-packs`, {
    credentials: "include",
  });
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to load credit packs");
  }
  return res.json();
}
